/* ============================================================
   YADA Website — Entry point
   ============================================================ */

// Font imports (must come first to avoid FOUT)
import '@fontsource/sora/400.css'
import '@fontsource/sora/700.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/700.css'
import '@fontsource/noto-sans-tc/400.css'
import '@fontsource/noto-sans-tc/700.css'
import '@fontsource/noto-sans-sc/400.css'
import '@fontsource/noto-sans-sc/700.css'
import '@fontsource/noto-serif-tc/700.css'
import '@fontsource/noto-serif-tc/900.css'

// Global styles (Tailwind v4 + tokens)
import './styles/globals.css'

// The ValuesIncubator+ page's colour system — the twelve printed Priority
// Values card colours, applied to that page. Imported here (not inside the
// page component) for the same reason as the sheets below: the prerender
// pass renders components in plain Node and cannot import CSS.
//
// It comes AFTER globals.css on purpose: it overrides the focus ring on
// `.pv-card`, and between two unlayered rules of equal specificity the
// later import wins. Every rule is scoped under `.vi-root`, so the deck's
// palette reaches this page and nothing else.
import './styles/values-incubator.css'

// SmartCindy's own stylesheet. Imported here rather than in the page
// component because the prerender pass renders components in plain Node,
// which cannot import CSS. Every rule is scoped under `.sc-root`, so the
// page's palette reaches the page and nothing else.
import './styles/smart-cindy.css'

// The 國民教育 pages' stylesheets, ported from the hand-coded standalone
// documents they replaced. Same reason as above: Node cannot import CSS
// during the prerender, so the import lives here.
//
// They come AFTER globals.css on purpose. globals.css carries a
// `:lang(zh-Hant) h2 { … }` typography layer whose specificity (0,1,1) ties
// with the scoped heading rules here (`.ne-root h2`, also 0,1,1) — and
// between two unlayered rules of equal specificity the later import wins.
// Reversing the order would hand the page's headings the site's weight and
// letter-spacing instead of the ones it was designed with.
import './styles/national-education.css'
import './styles/national-education-ks2.css'

// My Harbour's warm, "caring" palette. Imported here (not inside the
// page component) for the same reason as the sheets above: the
// prerender pass renders components in plain Node and cannot import
// CSS. Every rule is scoped under `.theme-harbour`, so the palette
// reaches the Harbour page and nothing else.
import './styles/harbour.css'

// CSP — 升學及職涯起航平台. Its own stylesheet, imported here (not inside
// the page component) for the same reason as the sheets above: the
// prerender pass renders components in plain Node, which cannot import
// CSS. Every rule is scoped under `.csp-root`, so the palette reaches
// the CSP page and nothing else.
import './styles/csp.css'

// BiteLearn — 每日微學習方案. Its own stylesheet, imported here (not inside
// the page component) for the same reason as the sheets above: the
// prerender pass renders components in plain Node, which cannot import
// CSS. It is a straight port of the standalone page's `css/styles.css`,
// with every rule scoped under `.bl-root`, so the page's cream / navy
// palette reaches the BiteLearn page and nothing else.
//
// It comes AFTER globals.css on purpose: globals.css carries a
// `:lang(zh-Hant) p, li { … }` typography layer and a site-wide
// `.section-title` rule that both have to be neutralised inside this
// page — and between two unlayered rules of equal specificity the later
// import wins.
import './styles/bite-learn.css'

// React + i18n bootstrap
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import App from './App'
import './i18n'

const root = document.getElementById('root')
if (!root) throw new Error('Root element #root not found in index.html')

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>,
)
